import React, { type PropsWithChildren } from 'react';
import { type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme, useResponsive } from '@sg/garnish';

import { useIsFoldableDevice } from '../../hooks/useIsFoldableDevice';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Keeps compact screen content clear of side insets, such as the iPhone Duo
 * system rail, with a light border along each inset edge.
 */
export const HorizontalSafeAreaContainer = (props: Props) => {
  const { children, style } = props;

  const { currentBreakpoint } = useResponsive();
  const { right: safeAreaRight, left: safeAreaLeft } = useSafeAreaInsets();
  const isFoldableDevice = useIsFoldableDevice();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const hasRightSafeArea = safeAreaRight > 0;
  const hasLeftSafeArea = safeAreaLeft > 0;
  const shouldApplySafeArea = currentBreakpoint.isXS && (hasRightSafeArea || hasLeftSafeArea);
  const hasRightBorder = shouldApplySafeArea && hasRightSafeArea;
  const hasLeftBorder = shouldApplySafeArea && hasLeftSafeArea;

  // Foldables keep the containers on both displays so folding doesn't remount the content.
  const shouldRenderContainers = shouldApplySafeArea || isFoldableDevice;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(() => {
    if (!shouldApplySafeArea) {
      return;
    }

    return { paddingRight: safeAreaRight, paddingLeft: safeAreaLeft };
  }, [safeAreaLeft, safeAreaRight, shouldApplySafeArea]);

  const containerStyles = [
    styles.container,
    shouldApplySafeArea ? styles.containerWithSafeArea : undefined,
    containerDynamicStyles,
    shouldApplySafeArea ? style : undefined,
  ];

  const contentContainerStyles = [
    styles.contentContainer,
    hasRightBorder ? styles.contentContainerWithBorderRight : undefined,
    hasLeftBorder ? styles.contentContainerWithBorderLeft : undefined,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  if (!shouldRenderContainers) {
    return children;
  }

  return (
    <View style={containerStyles}>
      <View style={contentContainerStyles}>{children}</View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    flexShrink: 1,
  },
  containerWithSafeArea: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  contentContainer: {
    flexGrow: 1,
    flexShrink: 1,
  },
  contentContainerWithBorderRight: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.LIGHT,
  },
  contentContainerWithBorderLeft: {
    borderLeftWidth: 1,
    borderLeftColor: theme.colors.LIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  style?: StyleProp<ViewStyle>;
}>;
